'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';

const outerRingTerms = ['REHABILITATION', 'CARETAKERS', 'WORK', 'AMANAH', 'ILM', 'FURQAN', 'TAWBA', 'MIZAN', 'FITRA', 'SUNNAH', 'HUKUM', 'AMR'];

export default function ResourceHero() {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };
    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay pt-20">
      {/* Cursor glow */}
      <div
        ref={cursorRef}
        style={{
          position: 'fixed',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 65%)',
          pointerEvents: 'none',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          willChange: 'transform',
        }}
      />
      <div className="pixel-border-gold-top" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
      {/* Outer ring vocabulary */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="-300 -300 600 600" width="700" height="700">
          {outerRingTerms?.map((term, i) => {
            const angle = (i / outerRingTerms?.length) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const r = 230;
            const x = r * Math.cos(rad);
            const y = r * Math.sin(rad);
            return (
              <text key={term} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                fill="rgba(179,138,42,0.8)" fontSize="9" fontFamily="monospace" fontWeight="900" opacity="0.25"
                letterSpacing="2" transform={`rotate(${angle + 90}, ${x}, ${y})`}
              >
                {term}
              </text>
            );
          })}
          {[80, 140, 200, 260]?.map((r) => (
            <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(139,105,20,0.12)" strokeWidth="0.5" opacity="1" />
          ))}
          {[0, 45, 90, 135]?.map((angle) => (
            <line key={angle}
              x1={-280 * Math.cos((angle * Math.PI) / 180)} y1={-280 * Math.sin((angle * Math.PI) / 180)}
              x2={280 * Math.cos((angle * Math.PI) / 180)} y2={280 * Math.sin((angle * Math.PI) / 180)}
              stroke="rgba(139,105,20,0.06)" strokeWidth="1" opacity="1"
            />
          ))}
          <circle cx="0" cy="0" r="65" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1" opacity="0.95" />
          <text x="0" y="-10" textAnchor="middle" fill="#F5F0E8" fontSize="10" fontFamily="monospace" fontWeight="900" opacity="0.9">MUSLIM</text>
          <text x="0" y="6" textAnchor="middle" fill="#F5F0E8" fontSize="8" fontFamily="monospace" fontWeight="900" opacity="0.9">RESOURCE</text>
          <text x="0" y="20" textAnchor="middle" fill="rgba(179,138,42,0.7)" fontSize="6" fontFamily="monospace" opacity="1" letterSpacing="2">GUIDE</text>
        </svg>
      </div>
      <div className={`relative z-10 max-w-[900px] mx-auto px-6 text-center transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{ color: 'rgba(139,105,20,0.7)' }}>07 — Community Utility</p>
        <h1 className="font-mono text-5xl md:text-7xl font-black uppercase tracking-tight leading-none mb-6" style={{ color: '#F5F0E8' }}>
          MUSLIM<br />
          <span className="text-shimmer">RESOURCE GUIDE</span>
        </h1>
        <p className="font-mono text-sm max-w-lg mx-auto leading-relaxed mb-12" style={{ color: 'rgba(245,240,232,0.5)' }}>
          Not a list of resources — a recognition that housing, education, and health are Ayat. Opportunities to uphold rights, remedy imbalance, renew the call to truth.
        </p>

        {/* 'Whole earth is a Masjid' intro */}
        <div className="max-w-xl mx-auto mb-8 p-5 rounded-lg" style={{ background: 'rgba(45,122,71,0.07)', border: '1px solid rgba(45,122,71,0.2)' }}>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: '#2d7a47', direction: 'rtl', textAlign: 'right', lineHeight: 1.8, marginBottom: 6 }}>
            جُعِلَتْ لِيَ الْأَرْضُ مَسْجِدًا وَطَهُورًا
          </p>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, fontStyle: 'italic', marginBottom: 4 }}>
            "The whole earth has been made a Masjid for me, and a means of purification."
          </p>
          <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
            — Hadith (Bukhari &amp; Muslim) · Every place of caretaking is a place of worship. The resource guide is an act of Ibadah.
          </p>
        </div>

        <div className="flex items-center justify-center gap-6">
          <Link href="#domains" className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
          >
            Explore Domains
          </Link>
          <Link href="/homepage" className="font-mono text-xs tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.4)', borderColor: 'rgba(139,105,20,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
          >
            ← Return to Hub
          </Link>
        </div>
      </div>
    </section>
  );
}
